import { Controller } from '@hotwired/stimulus';
import { postGeneratorOpenAI, getPaywallController } from '../lib/generator_api';
import {
  applyDisplayFormula,
  bindFormulaDelimiterListener,
  delimiterPromptInstruction,
  processGeneratedFormula,
  unbindFormulaDelimiterListener,
} from '../lib/generator_formula_delimiter';

const QUERY_SYSTEM_ROLE =
  'You generate Google Sheets QUERY formulas. Return only one valid formula starting with =QUERY(). Do not explain.';

const QUERY_PROMPT_PREFIX =
  'Generate a Google Sheets QUERY formula from the use case and range. Return only the formula. ' +
  String.raw`Examples:
Use case: Show customer and revenue sorted by revenue descending.
Range: A1:B1000
Response: =QUERY(A1:B1000, "select A, B where A is not null order by B desc", 1)
Use case: Count leads by source.
Range: A1:C2000
Response: =QUERY(A1:C2000, "select B, count(B) where B is not null group by B label count(B) 'Leads'", 1)
Use case: `;

export default class extends Controller {
  static targets = [
    'usecase',
    'range',
    'submit',
    'submitLabel',
    'placeholder',
    'loading',
    'output',
    'formulaText',
    'error',
    'successNote',
    'copyButton'
  ];

  static values = {
    tool: { type: String, default: 'query' }
  };

  connect() {
    bindFormulaDelimiterListener(this);
    this.formulaValue = '';
  }

  disconnect() {
    unbindFormulaDelimiterListener(this);
  }

  paywall() {
    return getPaywallController(this.element);
  }

  parseExample(event) {
    const raw = event.currentTarget.dataset.example;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      console.error('Invalid example payload');
      return null;
    }
  }

  loadExample(event) {
    const data = this.parseExample(event);
    if (!data) return;

    this.usecaseTarget.value = data.usecase;
    this.rangeTarget.value = data.range || 'A1';
    this.clearError();
    this.hideResult();
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
    this.usecaseTarget.focus();
  }

  async applyFormula(event) {
    const data = this.parseExample(event);
    if (!data?.formula) return;

    const { canonical, display } = processGeneratedFormula(data.formula);
    this.canonicalFormula = canonical;
    this.formulaValue = display;
    await this.copyToClipboard(display);
    this.showResult(display);
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
  }

  highlightExampleCard(button) {
    this.element.querySelectorAll('[data-example-card]').forEach((card) => {
      card.classList.remove('ring-2', 'ring-indigo-500/60', 'ring-offset-2');
    });
    button.closest('[data-example-card]')?.classList.add('ring-2', 'ring-indigo-500/60', 'ring-offset-2');
  }

  scrollToGenerator() {
    document.getElementById('query-generator')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const usecase = this.usecaseTarget.value.trim();
    if (!usecase) {
      this.showError('Describe the result you want before generating.');
      return;
    }

    const rangeRaw = this.rangeTarget.value;
    const rangeValue = !rangeRaw || rangeRaw.trim() === '' ? 'A1' : rangeRaw.trim();
    const prompt = `${QUERY_PROMPT_PREFIX}${delimiterPromptInstruction()}${usecase}\nRange: ${rangeValue}\nResponse: `;

    this.setLoading(true);
    this.hideResult();

    try {
      const result = await postGeneratorOpenAI({
        userRole: QUERY_SYSTEM_ROLE,
        prompt,
        generator: this.toolValue
      });

      if (result.limited) {
        this.paywall()?.handleLimitReached();
        return;
      }

      if (result.error) {
        console.error('QUERY generator proxy error', result.response?.status, result.detail);
        this.showError('Could not generate a formula. Try again in a moment.');
        return;
      }

      const data = result.data;
      const rawFormula = data.choices?.[0]?.message?.content?.trim();

      if (!rawFormula) {
        console.error('OpenAI proxy returned no QUERY formula', data);
        this.showError('No formula came back. Add more detail and try again.');
        return;
      }

      const { canonical, display } = processGeneratedFormula(rawFormula);
      this.canonicalFormula = canonical;
      this.formulaValue = display;
      await this.copyToClipboard(display);
      this.paywall()?.applyUsageFromResponse(data.generator_usage);
      this.showResult(display);

      if (data.generator_usage?.remaining === 1) {
        this.showSuccessNudge();
      }
      if (data.generator_usage?.remaining === 0) {
        this.paywall()?.handleLimitReached();
      }
    } catch (err) {
      console.error('QUERY request failed', err);
      this.showError('Connection failed. Check your network and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async copy(event) {
    event?.preventDefault();
    if (!this.formulaValue) return;

    await this.copyToClipboard(this.formulaValue);
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied again.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be blocked; formula remains visible in output panel.
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      const paywall = this.paywall();
      const atLimit = paywall && !paywall.paidMemberValue && paywall.remainingValue === 0;
      if (atLimit) {
        paywall.syncUi(0);
      } else {
        this.submitTarget.disabled = isLoading;
      }
    }
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isLoading ? 'Generating…' : 'Generate';
    }
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle('hidden', !isLoading);
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.toggle('hidden', isLoading);
    if (isLoading && this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (isLoading && this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (isLoading && this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  hideResult() {
    if (this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  showResult(formula) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    applyDisplayFormula(this, formula);
    if (this.hasOutputTarget) this.outputTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.remove('hidden');
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied to clipboard. Paste into your sheet.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showSuccessNudge() {
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Nice. 1 free generation left on this tool.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove('hidden');
    }
    this.hideResult();
  }
}
